const HEADER_ALIASES = {
  date: ['date', 'transaction date', 'posted date', 'posting date', 'effective date', 'datum', 'buchungsdatum', 'wertstellung'],
  description: ['description', 'details', 'memo', 'name', 'transaction', 'payee', 'beschreibung', 'verwendungszweck', 'buchungstext'],
  reference: ['reference', 'reference number', 'ref', 'confirmation', 'transaction number', 'referenz', 'kundenreferenz'],
  amount: ['amount', 'transaction amount', 'betrag', 'umsatz'],
  credit: ['credit', 'deposit', 'deposits', 'money in', 'gutschrift', 'einzahlung', 'haben'],
  debit: ['debit', 'withdrawal', 'withdrawals', 'money out', 'belastung', 'abbuchung', 'soll'],
  currency: ['currency', 'currency code', 'währung', 'waehrung'],
};

const normalizeHeader = (value) => String(value || '').trim().toLowerCase();

export function parseCsvRows(text) {
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '"') {
      if (quoted && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === ',' && !quoted) {
      row.push(field);
      field = '';
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      if (row.some((cell) => String(cell).trim() !== '')) rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  row.push(field);
  if (row.some((cell) => String(cell).trim() !== '')) rows.push(row);
  return rows;
}

function findColumn(headers, key) {
  const aliases = HEADER_ALIASES[key];
  return headers.findIndex((header) => aliases.includes(normalizeHeader(header)));
}

function parseMoney(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const negative = /^\(.*\)$/.test(raw) || /^-/.test(raw);
  let cleaned = raw.replace(/[^0-9.,-]/g, '').replace(/-/g, '');
  const comma = cleaned.lastIndexOf(',');
  const dot = cleaned.lastIndexOf('.');
  if (comma >= 0 && dot >= 0) {
    cleaned = comma > dot
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '');
  } else if (comma >= 0) {
    const decimalDigits = cleaned.length - comma - 1;
    cleaned = decimalDigits === 2 ? cleaned.replace(',', '.') : cleaned.replace(/,/g, '');
  }
  const numeric = Number(cleaned);
  if (!Number.isFinite(numeric)) return null;
  return Math.abs(numeric) * (negative ? -1 : 1);
}

function parseDate(value, format = 'auto') {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const iso = raw.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`;

  const parts = raw.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
  if (!parts) return null;
  let month;
  let day;
  if (format === 'dd-mm-yyyy') {
    day = Number(parts[1]);
    month = Number(parts[2]);
  } else if (format === 'mm-dd-yyyy') {
    month = Number(parts[1]);
    day = Number(parts[2]);
  } else if (Number(parts[1]) > 12) {
    day = Number(parts[1]);
    month = Number(parts[2]);
  } else {
    month = Number(parts[1]);
    day = Number(parts[2]);
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${parts[3]}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseBankCsv(text, { currency = 'CAD', dateFormat = 'auto' } = {}) {
  const rows = parseCsvRows(String(text || '').replace(/^\uFEFF/, ''));
  if (rows.length < 2) throw new Error('The CSV file has no transaction rows.');

  const headers = rows[0];
  const columns = Object.fromEntries(
    Object.keys(HEADER_ALIASES).map((key) => [key, findColumn(headers, key)])
  );
  if (columns.date < 0 || columns.description < 0) {
    throw new Error('CSV must contain Date and Description columns.');
  }
  if (columns.amount < 0 && columns.credit < 0) {
    throw new Error('CSV must contain Amount or Credit/Deposit columns.');
  }

  const rejected = [];
  const transactions = [];
  rows.slice(1).forEach((cells, index) => {
    const txDate = parseDate(cells[columns.date], dateFormat);
    let amount = columns.amount >= 0 ? parseMoney(cells[columns.amount]) : null;
    if (amount === null && columns.credit >= 0) amount = parseMoney(cells[columns.credit]);
    if ((amount === null || amount === 0) && columns.debit >= 0) {
      const debit = parseMoney(cells[columns.debit]);
      if (debit !== null) amount = -Math.abs(debit);
    }
    if (!txDate || amount === null) {
      rejected.push({ row: index + 2, reason: !txDate ? 'Invalid date' : 'Invalid amount' });
      return;
    }
    transactions.push({
      transaction_id: `csv-${index + 2}`,
      date: txDate,
      description: String(cells[columns.description] || '').trim(),
      reference: columns.reference >= 0 ? String(cells[columns.reference] || '').trim() : '',
      amount: Number(amount.toFixed(2)),
      currency: columns.currency >= 0
        ? String(cells[columns.currency] || currency).trim().toUpperCase().slice(0, 3)
        : currency,
    });
  });

  if (!transactions.length) throw new Error('No valid transactions were found in this CSV.');
  return { transactions, rejected };
}
